import i18n from '@/i18n';
import { notificationService } from '@/services/notification/notification-service';
import { CopyMonitorError } from '@/services/sharepoint-drive/copy-monitor-error';
import { directoryKeys } from '@/utils/queryKeys';
import { useQueryClient } from '@tanstack/vue-query';
import {
  inject,
  onBeforeUnmount,
  provide,
  ref,
  type InjectionKey,
  type Ref,
} from 'vue';
import { useCopyItemMutation } from '../../data/useCopyItemMutation';
import { computeCopyMode } from '../../permissions/modes';
import type { FileManagerPermissionsState } from '../../permissions/useFileManagerPermissions';
import type { FileSystemItem } from '../../types';
import { getParentPath } from '../../utils';
import {
  summarizeTransferFailures,
  type TransferResult,
} from '../transfer/useFileManagerTransfer';
import { splitName, withExtension } from './itemName';
import type { FileManagerMountsState } from './useFileManagerMounts';

const { t } = i18n.global;

// A trailing " (n)" on a base name — the shape this feature itself produces. Matched so that copying
// a copy climbs (`rapport (2)` -> `rapport (3)`) instead of nesting (`rapport (2) (2)`). The lazy
// group with an anchored tail means only the LAST index is stripped: `a (1) (2)` -> `a (1)`.
const COPY_INDEX_PATTERN = /^(.*?) \((\d+)\)$/;

// Bounds the search below so a pathological folder can never spin. Far beyond any real folder; on the
// (unreachable in practice) miss we fall back to " (2)" and let SharePoint's own conflictBehavior
// 'fail' surface the collision as the ordinary "finns redan" toast.
const MAX_COPY_INDEX = 1000;

/** Pure: drops a trailing " (n)" so copying a copy climbs rather than nests. */
export const stripCopyIndex = (base: string): string =>
  COPY_INDEX_PATTERN.exec(base)?.[1] ?? base;

/**
 * Pure: the base name the copy modal pre-fills — the lowest " (n)" (starting at 2, the original being
 * implicitly 1) whose FULL name is not already taken among `siblingNames`.
 *
 * Returns the BASE only, mirroring initialRenameValue: a file's extension is fixed and never offered
 * for editing, so the modal shows `rapport (2)` and submitCopy re-appends `.docx`. Collision checking
 * still happens on the full name, which is what actually has to be unique.
 *
 * Type-agnostic on purpose (D10): a directory simply has no extension to split off, so this already
 * works for folders on the day buildSingleFolderMenu starts offering the action.
 */
export const nextCopyBaseName = (
  item: FileSystemItem,
  siblingNames: string[],
): string => {
  const { base, ext } =
    item.type === 'file' ? splitName(item.name) : { base: item.name, ext: '' };
  const root = stripCopyIndex(base);
  const taken = new Set(siblingNames);

  for (let index = 2; index <= MAX_COPY_INDEX; index += 1) {
    const candidate = `${root} (${index})`;
    if (!taken.has(withExtension(candidate, ext))) return candidate;
  }
  return `${root} (2)`;
};

export type FileManagerCopyState = {
  isModalOpen: Ref<boolean>;
  pendingItem: Ref<FileSystemItem | null>;
  /**
   * The modal's pre-filled input value.
   *
   * A plain ref set in requestCopy, NOT a computed off pendingItem the way rename's initialName is:
   * it depends on the destination's sibling names, so it is a snapshot taken at the moment the menu
   * action fired rather than something that should silently re-derive later. Recomputing it while the
   * modal is open would also mean changing text under a user who is mid-edit.
   */
  initialName: Ref<string>;
  /** Path-scoped for the same reason as useFileManagerDelete's canDeleteAt — see its doc comment. */
  canCopyAt: (path: string) => boolean;
  /**
   * Source paths with a copy in flight. Drives the dimmed row state (DetailsTableView's
   * `.is-copying`), same convention as deletingPaths/renamingPaths.
   *
   * It dims the SOURCE, which an in-place duplicate does not itself change — deliberately, and with
   * precedent: a cross-mount drag-copy already dims a source it equally leaves alone. The row is the
   * only anchor on screen, since the thing actually pending does not exist yet.
   *
   * Also load-bearing beyond the visuals: call sites hide the Kopiera entry while a path is in here,
   * which is what stops a rapid second copy from pre-filling the same " (n)" the first one is about
   * to take (the cache read below cannot see an item that does not exist yet).
   */
  copyingPaths: Ref<Set<string>>;
  requestCopy: (item: FileSystemItem) => void;
  submitCopy: (newBaseName: string) => void;
  cancelCopy: () => void;
};

export const fileManagerCopyKey: InjectionKey<FileManagerCopyState> =
  Symbol('fileManagerCopy');

/**
 * Duplicating an item into its own folder under a new name — the context menu's "Kopiera".
 *
 * Deliberately NOT routed through provideFileManagerTransfer, despite that being the only other place
 * a copy runs. Two structural reasons: transferPolicyFor guarantees `source.key === destination.key`
 * means `operation: 'move'`, so an in-place copy would need a TransferPolicy that function can never
 * return; and runTransfer iterates a BATCH, where a single `name` is only coherent for one item. What
 * this duplicates instead is ~12 lines (an AbortController and the error classify), while the message
 * wording is shared for real via the exported, pure summarizeTransferFailures.
 *
 * Takes `permissions`/`mounts` as explicit params rather than self-injecting, for the same reason
 * provideFileManagerDelete documents: it is called directly in FileManager.vue's own setup(), and
 * inject() can never resolve a component's own provide(). Needs neither `navigation` (a copy never
 * moves the current folder) nor `details` (the panel keeps showing the original, which stays correct).
 */
export const provideFileManagerCopy = (
  permissions: Pick<FileManagerPermissionsState, 'permissionsAt'>,
  mounts: Pick<FileManagerMountsState, 'resolve'>,
) => {
  const mutation = useCopyItemMutation(mounts);
  const queryClient = useQueryClient();

  const isModalOpen = ref(false);
  const pendingItem = ref<FileSystemItem | null>(null);
  const initialName = ref('');
  const copyingPaths = ref<Set<string>>(new Set());

  // Abandons an in-flight copy MONITOR when the pane goes away — never the copy itself, which
  // SharePoint completes server-side regardless. Same reasoning as provideFileManagerTransfer's.
  const abortController = new AbortController();
  onBeforeUnmount(() => abortController.abort());

  const canCopyAt = (path: string) =>
    computeCopyMode(permissions.permissionsAt(path)) === 'enabled';

  const stopCopying = (path: string) => {
    const next = new Set(copyingPaths.value);
    next.delete(path);
    copyingPaths.value = next;
  };

  const classifyFailure = (
    item: FileSystemItem,
    error: unknown,
  ): TransferResult => {
    if (error instanceof CopyMonitorError && error.reason !== 'failed') {
      return {
        item,
        status: 'abandoned',
        reason: error.reason === 'timeout' ? 'timeout' : 'unmounted',
      };
    }
    return { item, status: 'failed', error };
  };

  // Hide-on-missing is enforced up the call chain (the builder omits the entry when the target is not
  // copyable), but this no-op guard is cheap insurance against a stale entry point calling through
  // after a permission change — same convention as requestDelete/requestRename.
  //
  // The gate asks about the PARENT: an in-place copy creates an item in the folder the source lives
  // in, so that is the path whose capabilities decide it.
  const requestCopy = (item: FileSystemItem) => {
    const parentPath = getParentPath(item.path);
    if (!canCopyAt(parentPath)) return;

    // Sibling names come straight from the children query's cache rather than being threaded in as an
    // argument: every entry point (grid row, details panel) then needs to know nothing about naming,
    // and the details panel has no item list of its own to pass. Always warm in practice — the user
    // just right-clicked a row in this very folder — and an empty fallback simply yields " (2)".
    const siblings =
      queryClient.getQueryData<FileSystemItem[]>(
        directoryKeys.children(parentPath),
      ) ?? [];

    pendingItem.value = item;
    initialName.value = nextCopyBaseName(
      item,
      siblings.map((sibling) => sibling.name),
    );
    isModalOpen.value = true;
  };

  const cancelCopy = () => {
    isModalOpen.value = false;
  };

  const runCopy = async (
    item: FileSystemItem,
    destinationParentPath: string,
    name: string,
  ) => {
    try {
      await mutation.mutateAsync({
        item,
        destinationParentPath,
        name,
        signal: abortController.signal,
      });
      // Silent on success: the new row appearing in the folder the user is already looking at IS the
      // feedback, the same convention rename/delete/create-folder follow. A drag-copy toasts only
      // because its destination is somewhere the user cannot see.
    } catch (error) {
      // Nobody is on screen to read any of this — the pane went away mid-copy.
      if (abortController.signal.aborted) return;

      const result = classifyFailure(item, error);
      const failureMessage = summarizeTransferFailures([result], 'copy');
      if (failureMessage) {
        notificationService.open({ type: 'danger', text: failureMessage });
        return;
      }
      // Not 'danger': we did not observe a failure, we stopped observing. The copy is still running
      // inside SharePoint and may well land.
      if (result.status === 'abandoned' && result.reason === 'timeout') {
        notificationService.open({
          type: 'info',
          text: t('fileManager.dragDrop.useFileManagerDrag.copyStillRunning'),
        });
      }
    } finally {
      stopCopying(item.path);
    }
  };

  const submitCopy = (newBaseName: string) => {
    const item = pendingItem.value;
    // Closes immediately (optimistic), same convention as rename/NewFolderModal — the modal never
    // stays open awaiting a round-trip that can run for minutes; a failure surfaces later as a toast.
    isModalOpen.value = false;
    if (!item) return;

    // The extension is fixed and was never offered for editing, so it is re-appended from the SOURCE
    // rather than parsed back out of whatever the user typed.
    const ext = item.type === 'file' ? splitName(item.name).ext : '';
    const name = withExtension(newBaseName, ext);

    copyingPaths.value = new Set(copyingPaths.value).add(item.path);
    void runCopy(item, getParentPath(item.path), name);
  };

  const state: FileManagerCopyState = {
    isModalOpen,
    pendingItem,
    initialName,
    canCopyAt,
    copyingPaths,
    requestCopy,
    submitCopy,
    cancelCopy,
  };
  provide(fileManagerCopyKey, state);
  return state;
};

export const useFileManagerCopy = () => {
  const state = inject(fileManagerCopyKey);
  if (!state) {
    throw new Error(
      'useFileManagerCopy() must be called within a component tree provided by provideFileManagerCopy()',
    );
  }
  return state;
};
